// Empire Apps Chassis · connectors/health/google-fit
// Google Fit REST API OAuth (existing Google client).
// Scopes:
//   - fitness.activity.read
//   - fitness.heart_rate.read
//   - fitness.sleep.read
//   - fitness.body.read

import {
  type ConnectorAdapter,
  type ConnectorMode,
  type ConnectorResult,
  type ConnectorStatus,
  devModeEnabled,
  err,
  fetchWithBudget,
  liveDisabled,
  nowIso,
  ok,
  shortHash,
} from './_types.ts';

export interface GoogleFitDay {
  readonly date: string;
  readonly steps: number;
  readonly sleepHours: number;
  readonly restingHeartRate: number;
  readonly activeMinutes: number;
  readonly distanceMeters: number;
}

export interface GoogleFitPayload {
  readonly daysTrailing30: readonly GoogleFitDay[];
  readonly weightKg: number;
}

export interface GoogleFitStatus extends ConnectorStatus {
  readonly id: 'google-fit';
  readonly scopesGranted: readonly string[];
}

const SCOPES = [
  'https://www.googleapis.com/auth/fitness.activity.read',
  'https://www.googleapis.com/auth/fitness.heart_rate.read',
  'https://www.googleapis.com/auth/fitness.sleep.read',
  'https://www.googleapis.com/auth/fitness.body.read',
];

export class GoogleFitConnector implements ConnectorAdapter<GoogleFitStatus, GoogleFitPayload> {
  readonly id = 'google-fit';
  readonly displayName = 'Google Fit';
  readonly displayNameHi = 'Google Fit · steps + sleep + heart';
  readonly dpdpCategory = 'PHI-medium · activity + sleep + heart rate';
  readonly mode: ConnectorMode;
  private connected = false;
  private lastSync: string | null = null;
  private principalHash: string | null = null;
  private accessToken: string | null = null;

  constructor(
    private readonly principalId: string,
    private readonly clientId: string | null = null,
  ) {
    this.mode = devModeEnabled() && this.clientId !== null ? 'live' : 'test';
  }

  async getStatus(): Promise<GoogleFitStatus> {
    if (!this.principalHash) this.principalHash = await shortHash(this.principalId);
    return {
      id: this.id,
      mode: this.mode,
      connected: this.connected,
      lastSyncAt: this.lastSync,
      principalIdHash: this.principalHash,
      notes: this.connected ? 'OAuth granted, daily data fetch active' : 'Google sign-in pending',
      scopesGranted: this.connected ? SCOPES : [],
    };
  }

  async connect(): Promise<ConnectorResult<GoogleFitStatus>> {
    if (this.mode === 'live') {
      if (!this.clientId) return err(liveDisabled(this.id));
      // OAuth implicit flow stub. The host folio must own the popup +
      // redirect URI registration. Once a token arrives via postMessage,
      // setAccessToken() is called.
      return err(liveDisabled(this.id));
    }
    this.connected = true;
    this.lastSync = nowIso();
    return ok(await this.getStatus());
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
    this.connected = true;
    this.lastSync = nowIso();
  }

  async disconnect(): Promise<void> {
    this.connected = false;
    this.lastSync = null;
    this.accessToken = null;
  }

  async importPayload(): Promise<ConnectorResult<GoogleFitPayload>> {
    if (this.mode === 'live' && this.accessToken) {
      try {
        const days = await this.fetchDays();
        return ok({ daysTrailing30: days, weightKg: 0 });
      } catch (e) {
        return err({ code: 'network', message: String(e instanceof Error ? e.message : e), retriable: true });
      }
    }
    return ok(this.mockPayload());
  }

  private async fetchDays(): Promise<readonly GoogleFitDay[]> {
    const end = Date.now();
    const start = end - 30 * 24 * 3600 * 1000;
    const body = {
      aggregateBy: [
        { dataTypeName: 'com.google.step_count.delta' },
        { dataTypeName: 'com.google.heart_rate.bpm' },
        { dataTypeName: 'com.google.sleep.segment' },
        { dataTypeName: 'com.google.distance.delta' },
        { dataTypeName: 'com.google.active_minutes' },
      ],
      bucketByTime: { durationMillis: 86_400_000 },
      startTimeMillis: start,
      endTimeMillis: end,
    };
    const res = await fetchWithBudget(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify(body),
      },
      { retries: 2, timeoutMs: 12_000 },
    );
    if (!res.ok) throw new Error(`google-fit ${res.status}`);
    const json = (await res.json()) as { bucket?: readonly { startTimeMillis: string; dataset: readonly { point: readonly unknown[] }[] }[] };
    const days: GoogleFitDay[] = [];
    for (const b of json.bucket ?? []) {
      days.push({
        date: new Date(Number(b.startTimeMillis)).toISOString().slice(0, 10),
        steps: 0,
        sleepHours: 0,
        restingHeartRate: 0,
        activeMinutes: 0,
        distanceMeters: 0,
      });
    }
    return days;
  }

  mockPayload(): GoogleFitPayload {
    const today = new Date();
    const days: GoogleFitDay[] = [];
    for (let d = 29; d >= 0; d--) {
      const date = new Date(today);
      date.setDate(today.getDate() - d);
      const v = (Math.sin(d * 0.5) + 1) * 0.5;
      days.push({
        date: date.toISOString().slice(0, 10),
        steps: 5500 + Math.round(v * 5000),
        sleepHours: 6.3 + v * 1.4,
        restingHeartRate: 58 + Math.round(v * 7),
        activeMinutes: 22 + Math.round(v * 28),
        distanceMeters: 3500 + Math.round(v * 3000),
      });
    }
    return { daysTrailing30: days, weightKg: 72.4 };
  }
}
